import { useEffect, useState } from "react";
import {
  useAssignedClientSelection,
  useBusyAction,
  useFetch,
  useRequest,
} from "../../../hooks";
import { DownloadAction } from "../../../components/shared/DocRowActions";
import { usePaginator } from "../../../hooks/usePaginator";
import { WellProps, DocRow } from "../../../interfaces/interfaces";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import { dateInRange, displayDate, fmtDate } from "../../../utils/docDate";

interface WellsFetchResponse {
  success?: boolean;
  data: WellProps[];
}

export default function ChemicalTrackerClient() {
  const { handleRequest } = useRequest();
  const {
    clientOptions,
    selectedClientId,
    setSelectedClientId,
    showClientFilter,
  } = useAssignedClientSelection();
  const { paginationModel, setPaginationModel } = usePaginator("chemical-tracker-client", 8);

  const { data: wellsResp, isLoading: wellsLoading } =
    useFetch<WellsFetchResponse>(
      selectedClientId ? `well/client/${selectedClientId}` : undefined,
    );
  const wells: WellProps[] = wellsResp?.data ?? [];

  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);

  // Filter/search state — previously owned internally by DocumentsPortalLayout,
  // now lifted up because ListPageLayout is a controlled component: it renders
  // whatever `rows` slice it's given and expects the caller to own filtering,
  // search and pagination.
  const [docSearch, setDocSearch] = useState("");
  const [owner, setOwner] = useState(""); // "" = all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const getWellName = (idWell?: number) =>
    wells.find((w) => w.id === idWell)?.name ?? "";

  // Fetch-model fix: fetch all of the assigned client's chemical trackers once,
  // via the same `chemical-list/client/${clientId}` endpoint shape
  // ChemicalTrackerAdmin uses, rather than re-fetching per well selection. The
  // wells side panel below now filters the already-loaded rows client-side,
  // matching Admin and every other sub-feature in this family.
  useEffect(() => {
    setSelectedWellId(null);
    if (!selectedClientId) { setRows([]); return; }
    setRowsLoading(true);
    handleRequest({ endpoint: `chemical-list/client/${selectedClientId}`, options: { method: "GET" } })
      .then((res) => {
        const items: any[] = Array.isArray(res.data) ? res.data : res.data?.rows ?? [];
        const mapped: DocRow[] = items.map((item: any, idx: number) => ({
          rowId: `c-${item.id ?? idx}`,
          customName: item.customName,
          date: displayDate(item),
          uploadedAt: item.date ?? "",
          wellId: item.idWell,
          wellName: getWellName(item.idWell),
          source: "uploaded" as const,
          path: item.path,
          owner: item.user?.name ?? "—",
        }));
        setRows(mapped.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  useEffect(() => {
    if (!wells.length || !rows.length) return;
    setRows((prev) =>
      prev.map((r) => (r.wellName ? r : { ...r, wellName: getWellName(r.wellId) })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wells]);

  const downloadFile = (path: string, name: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const opts: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key: path }),
        headers: { responseType: "Buffer" } as any,
      };
      handleRequest({ endpoint: "pdf-chemical-adm", options: opts })
        .then((res) => {
          if (!res.success) { reject(new Error("Failed")); return; }
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", name.endsWith(".pdf") ? name : `${name}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          resolve();
        })
        .catch(reject);
    });

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.path) await downloadFile(row.path, row.customName);
  };
  const { isBusy: isDownloading, run: runDownload } = useBusyAction(handleDownload);

  // ---- Derived: well filter -> search -> owner/date filters ----
  const filteredByWell =
    selectedWellId != null ? rows.filter((r) => r.wellId === selectedWellId) : rows;

  let filtered = docSearch.trim()
    ? filteredByWell.filter((r) =>
        r.customName.toLowerCase().includes(docSearch.trim().toLowerCase()),
      )
    : filteredByWell;
  if (owner) filtered = filtered.filter((r) => r.owner === owner);
  if (dateFrom || dateTo) {
    filtered = filtered.filter((r) => {
      return dateInRange(r.date, dateFrom, dateTo);
    });
  }

  const ownerOptions = Array.from(new Set(rows.map((r) => r.owner))).filter(
    (o) => o && o !== "—",
  );

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const pageRows = filtered.slice(
    currentPage * paginationModel.pageSize,
    (currentPage + 1) * paginationModel.pageSize,
  );

  const columns: ListColumn<DocRow>[] = [
    { key: "customName", header: "Chemical Tracker", render: (r) => r.customName, width: "2fr" },
    { key: "wellName", header: "Well", render: (r) => r.wellName || "—", width: "160px" },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date), width: "120px" },
    { key: "owner", header: "Created by", render: (r) => r.owner, width: "160px" },
  ];

  const filters: ListFilterField[] = [
    { kind: "date", key: "dateFrom", label: "From", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To", value: dateTo, onChange: setDateTo },
  ];
  if (ownerOptions.length > 0) {
    filters.unshift({
      kind: "select",
      key: "owner",
      label: "Created by",
      value: owner,
      onChange: setOwner,
      options: ownerOptions.map((o) => ({ value: o, label: o })),
    });
  }

  return (
    <ListPageLayout<DocRow>
      title="Chemical Trackers"
      subtitle="Browse, filter and download client chemical trackers"
      columns={columns}
      rows={pageRows}
      getRowId={(r) => r.rowId}
      rowsLoading={rowsLoading}
      isError={isError}
      searchValue={docSearch}
      onSearchChange={setDocSearch}
      searchPlaceholder="Search chemical trackers…"
      filters={filters}
      page={currentPage}
      pageSize={paginationModel.pageSize}
      totalCount={totalCount}
      onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
      onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
      rowsPerPageOptions={[8, 15, 25, 50]}
      sidePanel={{
        title: "Wells",
        items: wells.map((w) => ({ id: w.id ?? -1, label: w.name })),
        selectedId: selectedWellId,
        onSelect: (id) => {
          setSelectedWellId(id as number | null);
          setPaginationModel({ ...paginationModel, page: 0 });
        },
        loading: wellsLoading,
        searchPlaceholder: "Search wells…",
      }}
      toolbarActions={
        showClientFilter && clientOptions?.length ? (
          <select
            value={selectedClientId ? String(selectedClientId) : ""}
            onChange={(e) => {
              const c =
                clientOptions.find((cl) => String(cl.id) === e.target.value) ?? null;
              setSelectedClientId(c?.id ?? null);
            }}
          >
            <option value="" disabled>Select a client...</option>
            {clientOptions.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        ) : undefined
      }
      rowActions={(r) => (
        <DownloadAction rowId={r.rowId} busy={isDownloading} onClick={() => runDownload(r)} />
      )}
    />
  );
}
